import React from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

/**
 * App-wide error boundary. Without this, any uncaught render error blanks the
 * entire screen (the "black screen" symptom). Instead we show a recoverable
 * message so the user can reload or go back to the dashboard.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught render error:", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground"
          data-testid="app-error-boundary"
        >
          <div className="max-w-md w-full text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-sm border border-destructive/40 bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <h1 className="font-display font-semibold text-xl mb-2">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mb-6">
              This screen hit an unexpected error. Your data is safe — reload to continue, or head
              back to the dashboard.
            </p>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-sm bg-signal-500 text-white hover:bg-signal-500/90 transition-colors"
                data-testid="error-reload-btn"
              >
                <RotateCw className="w-3.5 h-3.5" /> Reload page
              </button>
              <button
                onClick={() => { this.handleReset(); window.location.assign("/app"); }}
                className="text-sm px-4 py-2 rounded-sm border border-border hover:bg-muted/40 transition-colors"
                data-testid="error-dashboard-btn"
              >
                Go to dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
